import { theme } from 'lib/theme';
import * as React from 'react';
import { Platform, StyleProp, View, ViewStyle } from 'react-native';
import Animated, {
  Easing,
  interpolate,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { Circle, G, Svg } from 'react-native-svg';

type Props = React.ComponentPropsWithRef<typeof View> & {
  color?: string;
  size?: 'small' | 'large' | number;
  style?: StyleProp<ViewStyle>;
};

export const ActivityIndicator: React.FC<Props> = ({
  color: indicatorColor,
  size: indicatorSize = 'small',
  style,
}) => {
  const size =
    typeof indicatorSize === 'string'
      ? indicatorSize === 'small'
        ? 24
        : 48
      : indicatorSize || 24;
  const CIRCUMFERENCE = size * Math.PI;
  const R = CIRCUMFERENCE / (2 * Math.PI);
  const STROKE_WIDTH = 8;
  const HALF_CIRCLE = R + STROKE_WIDTH;
  const DIAMETER = 2 * HALF_CIRCLE;

  const progress = useSharedValue(0);
  const rotation = useSharedValue(0);

  const startAnimation = React.useCallback(() => {
    progress.value = withRepeat(
      withSequence(
        withTiming(0.7, { duration: 800 }),
        withTiming(0.1, { duration: 2000 }),
      ),
      -1,
      true,
    );
    rotation.value = withRepeat(
      withTiming(1, { duration: 900, easing: Easing.linear }),
      -1,
      false,
    );
  }, [progress, rotation]);

  React.useEffect(() => {
    startAnimation();
  }, [startAnimation]);

  const AnimatedCircle = Animated.createAnimatedComponent(Circle);

  const animatedCircleProps = useAnimatedProps(() => {
    return {
      strokeDashoffset: CIRCUMFERENCE * (1 - progress.value),
    };
  });

  const animatedViewStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { rotate: `${interpolate(rotation.value, [0, 1], [0, 360])}deg` },
      ],
    };
  });

  const color = indicatorColor || theme.colors.blue90;

  return (
    <View
      style={[
        {
          justifyContent: 'center',
          alignItems: 'center',
        },
        style,
      ]}
      accessible
      accessibilityRole="progressbar"
      accessibilityState={{ busy: true }}
    >
      <Animated.View style={animatedViewStyle}>
        <Svg
          width={DIAMETER}
          height={DIAMETER}
          viewBox={`0 0 ${DIAMETER} ${DIAMETER}`}
        >
          <G transformOrigin={`${HALF_CIRCLE}, ${HALF_CIRCLE}`}>
            <AnimatedCircle
              cx="50%"
              cy="50%"
              r={R}
              strokeDashoffset={Platform.OS === 'web' ? size : undefined}
              animatedProps={Platform.OS === 'web' ? {} : animatedCircleProps}
              strokeWidth={STROKE_WIDTH}
              stroke={color}
              fill="transparent"
              strokeDasharray={CIRCUMFERENCE}
            />
          </G>
        </Svg>
      </Animated.View>
    </View>
  );
};
